import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReceiptImportWizard } from "@/components/financial/import/ReceiptImportWizard";
import { DocumentImportWorkspace } from "@/components/import/DocumentImportWorkspace";
import { Eyebrow } from "@/components/import/ImportPrimitives";
import { requireReachableRoute } from "@/lib/offline/routeGuard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/transactions/import",
)({
	// Receipt OCR is desk/capture work (upload + AI parsing) — online-only.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: ReceiptImportPage,
});

function ReceiptImportPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const navigate = useNavigate();

	// One stable draft-anchor id for the whole wizard session, so the upload and
	// the polling media list agree on the entity to look under.
	const [draftId] = useState(() => crypto.randomUUID());

	return (
		<div className="mx-auto flex w-full max-w-[1080px] flex-col gap-4 md:gap-6">
			<div className="flex flex-col gap-1 md:gap-1.5">
				<Eyebrow>{t("financial.receiptImport.eyebrow")}</Eyebrow>
				<h1
					className="font-display text-[clamp(24px,4vw,40px)] font-extrabold leading-[1.1] tracking-[-0.02em] text-base-content"
					data-testid="page-title"
				>
					{t("financial.receiptImport.pageTitle")}
				</h1>
				<p className="max-w-[56ch] text-sm leading-relaxed text-base-content/70 md:text-[15px]">
					{t("financial.receiptImport.pageSubtitle")}
				</p>
			</div>
			<DocumentImportWorkspace
				orgId={orgId}
				kind="transactions"
				onRecordOpen={(transactionId) => {
					void navigate({
						to: "/app/org/$orgId/financial/transactions/$transactionId",
						params: { orgId, transactionId },
					});
				}}
			>
				{({ resume, sessionKey, onUploaded, onDone, onCancelCurrent }) => (
					<ReceiptImportWizard
						key={`${resume?.id ?? "new"}-${sessionKey}`}
						orgId={orgId}
						draftId={resume?.entityId ?? draftId}
						resume={
							resume
								? {
										mediaFileId: resume.id,
										status: resume.status as
											| "PROCESSING"
											| "READY_FOR_REVIEW"
											| "FAILED",
										extraction: resume.result,
									}
								: undefined
						}
						onUploaded={onUploaded}
						onCancel={onCancelCurrent}
						onDone={() => {
							onDone();
							void navigate({
								to: "/app/org/$orgId/financial/transactions",
								params: { orgId },
							});
						}}
					/>
				)}
			</DocumentImportWorkspace>
		</div>
	);
}
